import { MultiPolygon, Geometry } from 'ol/geom';
import { FEATURE_ACTION_TYPE } from 'config';
import { makeAPICall, makeMockAPICall } from 'models/api';
import { BFMCMin } from 'models/bfmc';
import {
    API_URL,
    DATA_PROJECTION,
    GEOSERVER_URL,
    MAP_PROJECTION,
} from 'config';
import { Feature } from 'ol';
import { GeoJSON as olGeoJson } from 'ol/format';
import {
    Feature as GeoJsonFeature,
    GeoJsonProperties,
    Geometry as GeoJsonGeometry,
} from 'geojson';

export enum StrategyEnum {
    'AP',
    'SF',
    'LM',
    'IM',
    'LF',
    'TF',
    'GP',
    'FE',
    'NY',
}

export type StrategyId = keyof typeof StrategyEnum;

export declare namespace API {
    export interface FuelManagementRegister {
        id: number;
        name: string;
        strategy: StrategyId;
        priority: string;
        bmc: BFMCMin;
        focus_area: string;
        resp_agency: string;
        year: string;
        source: string;
        comment: string;
        status: string;
        bmc_id: number;
        plan_id: string;
        global_id: string;
        guardian_id: string;
        created_by: string;
        modified_by: string;
        bfrmp_id: string;
        action_type: FEATURE_ACTION_TYPE;
    }

    export interface FuelManagementRegisterAuditRecord {
        id: number;
        plan_id: string;
        feature_global_id: string;
        username: string;
        change_type: string;
        changed_on: string;
        reason_for_change: string;
    }

    export interface ActionResponse {
        success: boolean;
        error?: string;
    }

    export interface DataUpload {
        id: number;
        upload_id: string;
        filename: string;
        upload_type: string;
        uploaded_by: string;
        bmc_id: number;
        status: string;
        error_msg: string;
        uploaded_by_user: string;
    }
}

/**
 * Links the zone id with the same for
 * the Autocomplete/Dropdown for selecting
 * the Zone
 */
export interface Strategy {
    id: StrategyId;
    name: string;
}

export const strategies: Strategy[] = [
    {
        id: 'AP',
        name: 'Asset Protection Zone (APZ)',
    },
    {
        id: 'SF',
        name: 'Strategic Fire Advantage Zone (SFAZ)',
    },
    {
        id: 'LM',
        name: 'Land Management Zone (LMZ)',
    },
    {
        id: 'IM',
        name: 'Ignition Management Zone (IMZ)',
    },
    {
        id: 'LF',
        name: 'Linear Fire Break',
    },
    {
        id: 'TF',
        name: 'Transport Corridor Fire Break',
    },
    {
        id: 'GP',
        name: 'Grazing Program',
    },
    {
        id: 'FE',
        name: 'Fire Exclusion Zone (FEZ)',
    },
    {
        id: 'NY',
        name: 'Not Yet Decided (NYD)',
    },
];

export interface FuelManagementRegister {
    id: number;
    name: string;
    strategy: StrategyId;
    priority: string;
    bfmcId: number;
    planId: string;
    globalId: string;
    focusArea: string;
    respAgency: string;
    year: string;
    source: string;
    comment: string;
    status: string;
    guardianId: string;
    createdBy: string;
    modifiedBy: string;
    geometry?: MultiPolygon;
    bfrmpId?: string;
}

export interface FuelManagementRegisterAuditRecord {
    id: number;
    planId: string;
    featureGlobalId: string;
    username: string;
    changeType: string;
    changedOn: Date;
    reasonForChange: string;
}

export interface FuelManagementRegisterFilters {
    skip?: number;
    limit?: number;
    planId?: string;
    featureID?: number;
}

export interface FuelManagementRegisterBatchUpdate {
    bfmcId: number;
    status: string;
}

export interface FuelManagementRegisterAuditFields {
    planId?: string;
    globalFeatureID?: string;
}

export interface FMRUpload {
    bfmcId: number;
    planId: string;
    uploadType: 'APPEND' | 'REPLACE';
    dataFile: File;
    reasonForChange: string;
}

export interface GetFMRUploads {
    bfmcId: number;
    planId: string;
}

const FUEL_MANGEMENT_REGISTER_ROUTE = `${API_URL}/fuelmanagement-registers`;

export const mapAPIToFuelManagementRegister = ({
    bmc: _unused,
    bmc_id,
    focus_area,
    resp_agency,
    guardian_id,
    plan_id,
    created_by,
    modified_by,
    bfrmp_id,
    global_id,
    ...rest
}: API.FuelManagementRegister): FuelManagementRegister => ({
    ...rest,
    focusArea: focus_area,
    respAgency: resp_agency,
    guardianId: guardian_id,
    bfmcId: bmc_id,
    planId: plan_id,
    globalId: global_id,
    createdBy: created_by,
    modifiedBy: modified_by,
    bfrmpId: bfrmp_id,
});

export const mapAPIToFuelManagementRegisterAudit = ({
    plan_id,
    change_type,
    changed_on,
    feature_global_id,
    reason_for_change,
    ...rest
}: API.FuelManagementRegisterAuditRecord): FuelManagementRegisterAuditRecord => ({
    ...rest,
    planId: plan_id,
    changeType: change_type,
    changedOn: new Date(changed_on),
    featureGlobalId: feature_global_id,
    reasonForChange: reason_for_change,
});

export const deleteFMRegister = makeAPICall<
    API.ActionResponse,
    { id: number; reason_for_change: string },
    API.ActionResponse
>(
    ({ id, reason_for_change }) => ({
        url: `${FUEL_MANGEMENT_REGISTER_ROUTE}/${id}`,
        contentType: 'application/json',
        requestData: {
            method: 'DELETE',
            body: JSON.stringify({ reason_for_change }),
        },
    }),
    (x) => x,
);

interface FuelManagementRegisterUpdate {
    id: number;
    feature: GeoJsonFeature<GeoJsonGeometry, GeoJsonProperties>;
    reason_for_change: string;
    action_type: FEATURE_ACTION_TYPE;
}

export const editFmr = makeAPICall<
    FuelManagementRegister,
    FuelManagementRegisterUpdate,
    API.FuelManagementRegister
>(
    ({ id, feature, reason_for_change, action_type }) => {
        return {
            url: `${FUEL_MANGEMENT_REGISTER_ROUTE}/${id}`,
            contentType: 'application/json',
            requestData: {
                method: 'PUT',
                body: JSON.stringify({
                    feature,
                    reason_for_change,
                    action_type,
                }),
            },
        };
    },
    (x) => mapAPIToFuelManagementRegister(x),
);

interface FuelManagementRegisterCreate {
    feature: GeoJsonFeature<GeoJsonGeometry, GeoJsonProperties>;
}

export const addFmr = makeAPICall<
    FuelManagementRegister,
    FuelManagementRegisterCreate,
    API.FuelManagementRegister
>(
    ({ feature }) => {
        return {
            url: `${FUEL_MANGEMENT_REGISTER_ROUTE}`,
            contentType: 'application/json',
            requestData: {
                method: 'POST',
                body: JSON.stringify({ feature }),
            },
        };
    },
    (x) => mapAPIToFuelManagementRegister(x),
);

export const getFuelManagementRegisterList = makeAPICall<
    FuelManagementRegister[],
    FuelManagementRegisterFilters,
    API.FuelManagementRegister[]
>(
    ({ skip, limit, planId }) => {
        const params = new URLSearchParams();
        if (skip != null) params.append('skip', `${skip}`);
        if (limit != null) params.append('limit', `${limit}`);
        if (planId != null) params.append('plan_id', `${planId}`);
        return {
            url: `${FUEL_MANGEMENT_REGISTER_ROUTE}/?${params.toString()}`,
        };
    },
    (raw) => raw.map((fmr) => mapAPIToFuelManagementRegister(fmr)),
);

export const getFuelManagementRegisterAudit = makeAPICall<
    FuelManagementRegisterAuditRecord[],
    FuelManagementRegisterAuditFields,
    API.FuelManagementRegisterAuditRecord[]
>(
    ({ planId, globalFeatureID }) => {
        return {
            url: `${FUEL_MANGEMENT_REGISTER_ROUTE}/audit/${planId}/${globalFeatureID}`,
        };
    },
    (raw) => raw.map((record) => mapAPIToFuelManagementRegisterAudit(record)),
);

export const getFuelManagementRegisterFromGeoserver = makeAPICall<
    FuelManagementRegister[],
    FuelManagementRegisterFilters,
    any
>(
    ({ planId }) => {
        const params = new URLSearchParams({
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeName: 'risk-planning:FuelManagementRegister',
            outputFormat: 'application/json',
            srsname: DATA_PROJECTION.getCode(),
            CQL_FILTER: `${planId != null ? `PlanId='${planId}'` : ''}`,
        });
        return {
            url: `${GEOSERVER_URL}/wfs?${params.toString()}`,
        };
    },

    (raw) =>
        (raw.features as any[]).map<FuelManagementRegister>(
            ({ id, properties }) => ({
                id: +(id as string).split('.')[1],
                name: properties.Name as string,
                strategy: properties.Strategy as StrategyId,
                priority: properties.Priority as string,
                // WFS feature property is "BmcId" (post BFMC→BMC rename)
                bfmcId: properties.BmcId as number,
                planId: properties.PlanId,
                focusArea: properties.FocusArea as string,
                respAgency: properties.RespAgency as string,
                year: properties.Year,
                source: properties.Source,
                comment: properties.Comment,
                status: properties.Status,
                guardianId: properties.GuardianID,
                globalId: properties.GlobalId,
                createdBy: properties.CreatedBy as string,
                modifiedBy: properties.ModifiedBy as string,
            }),
        ),
);

export const getSelectedFeatureFromGeoserver = makeAPICall<
    Feature<Geometry>,
    FuelManagementRegisterFilters,
    any
>(
    ({ featureID }) => {
        const params = new URLSearchParams({
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeName: 'risk-planning:FuelManagementRegister',
            outputFormat: 'application/json',
            srsname: DATA_PROJECTION.getCode(),
            featureID: `${featureID}`,
        });
        return {
            url: `${GEOSERVER_URL}/wfs?${params.toString()}`,
        };
    },
    (raw) => {
        return new olGeoJson({
            dataProjection: DATA_PROJECTION,
            featureProjection: MAP_PROJECTION,
        }).readFeatures(raw)?.[0];
    },
);

export const batchUpdateFMRegister = makeAPICall<
    Feature<Geometry>,
    FuelManagementRegisterBatchUpdate,
    any
>(
    ({ bfmcId }) => {
        const params = new URLSearchParams({
            service: 'WFS',
            version: '1.0.0',
            request: 'GetFeature',
            typeName: 'risk-planning:FuelManagementRegister',
            outputFormat: 'application/json',
            srsname: DATA_PROJECTION.getCode(),
            featureID: `${bfmcId}`,
        });
        return {
            url: `${GEOSERVER_URL}/wfs?${params.toString()}`,
        };
    },
    (raw) => {
        return new olGeoJson({
            dataProjection: DATA_PROJECTION,
            featureProjection: MAP_PROJECTION,
        }).readFeatures(raw)?.[0];
    },
);

export const exportData = makeAPICall<
    ArrayBuffer,
    {
        queryParams: string;
        apiEndPoint: string;
        contentType: string;
    },
    ArrayBuffer
>(
    ({ queryParams, apiEndPoint, contentType }) => {
        return {
            url: `${FUEL_MANGEMENT_REGISTER_ROUTE}/${apiEndPoint}?${queryParams}`,
            dataType: 'blob',
            contentType: contentType,
        };
    },
    (data) => {
        return data;
    },
);

export const uploadFMRegister = makeAPICall<
    API.ActionResponse,
    FMRUpload,
    API.ActionResponse
>(
    ({ bfmcId, planId, uploadType, dataFile, reasonForChange }) => {
        const formData = new FormData();
        formData.append('data_file', dataFile);
        formData.append('upload_type', uploadType);
        formData.append('reason_for_change', reasonForChange);
        return {
            url: `${FUEL_MANGEMENT_REGISTER_ROUTE}/data-upload/${bfmcId}/${planId}`,
            contentType: 'auto',
            requestData: {
                method: 'POST',
                body: formData,
            },
        };
    },
    (data) => {
        return data;
    },
);

export interface DataUpload {
    id: number;
    uploadId: string;
    uploadType: string;
    filename: string;
    status: string;
    errorMessage: string;
    uploadedByUser: string;
}

export const mapAPIToFMRUpload = ({
    upload_id,
    upload_type,
    error_msg,
    uploaded_by_user,
    ...rest
}: API.DataUpload): DataUpload => ({
    ...rest,
    uploadId: upload_id,
    uploadType: upload_type,
    errorMessage: error_msg,
    uploadedByUser: uploaded_by_user,
});

export const getFMRUploads = makeAPICall<
    DataUpload[],
    GetFMRUploads,
    API.DataUpload[]
>(
    ({ bfmcId, planId }) => {
        return {
            url: `${FUEL_MANGEMENT_REGISTER_ROUTE}/data-upload/${bfmcId}/${planId}?limit=3`,
        };
    },
    (raw) => raw.map(mapAPIToFMRUpload),
);

export interface EditFMR {
    feature: Feature<Geometry> | null;
}

export const editFMRDialog = makeMockAPICall<EditFMR, EditFMR, null>(
    () => {},
    (_, payload) => payload,
    null,
    0,
);
